import type { MatterType, PractitionerFormFieldShowWhen } from '@shared/types';
import type { BackendRouteDefinition } from '../types';

export type PractitionerFormFieldPayload = {
  id: string;
  type:
    | 'short_text'
    | 'long_text'
    | 'first_name'
    | 'last_name'
    | 'email'
    | 'phone'
    | 'dropdown'
    | 'multiple_choice'
    | 'checkboxes'
    | 'linear_scale'
    | 'date'
    | 'time';
  label: string;
  required: boolean;
  placeholder?: string;
  options?: string[];
  scaleMin?: number;
  scaleMax?: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
  showWhen?: PractitionerFormFieldShowWhen;
};

export type PractitionerFormDefinitionSettingsPayload = {
  /** Intake only: matter type for new matters; omit to use Don't Know. */
  defaultMatterType?: MatterType;
};

export type CreateFirmFormDefinitionRequest = {
  title: string;
  mode: 'intake_new_matter' | 'questionnaire_existing_matter';
  fields?: PractitionerFormFieldPayload[];
  settings?: PractitionerFormDefinitionSettingsPayload;
};

export type PatchFirmFormDefinitionRequest = {
  formId: string;
  title?: string;
  fields?: PractitionerFormFieldPayload[];
  settings?: PractitionerFormDefinitionSettingsPayload | null;
};

export const listFirmFormDefinitionsRoute = {
  transport: 'api',
  method: 'GET',
  path: '/firms/form-definitions',
} as const satisfies BackendRouteDefinition;

export const listFirmFormSubmissionsRoute = {
  transport: 'api',
  method: 'GET',
  path: '/firms/form-submissions',
  query: (requestData: {
    formId?: string;
    matterId?: string;
    limit?: number;
  }) => {
    const query: Record<string, string | number> = {};
    if (requestData.formId !== undefined && requestData.formId.length > 0) {
      query.formId = requestData.formId;
    }
    if (requestData.matterId !== undefined && requestData.matterId.length > 0) {
      query.matterId = requestData.matterId;
    }
    if (requestData.limit !== undefined) {
      query.limit = requestData.limit;
    }
    return query;
  },
} as const satisfies BackendRouteDefinition;

export const createFirmFormDefinitionRoute = {
  transport: 'api',
  method: 'POST',
  path: '/firms/form-definitions',
  body: (requestData: CreateFirmFormDefinitionRequest) => requestData,
} as const satisfies BackendRouteDefinition;

export const getFirmFormDefinitionRoute = {
  transport: 'api',
  method: 'GET',
  path: '/firms/form-definitions/:formId',
  params: (requestData: { formId: string }) => ({ formId: requestData.formId }),
} as const satisfies BackendRouteDefinition;

export const patchFirmFormDefinitionRoute = {
  transport: 'api',
  method: 'PATCH',
  path: '/firms/form-definitions/:formId',
  params: (requestData: PatchFirmFormDefinitionRequest) => ({
    formId: requestData.formId,
  }),
  body: (requestData: PatchFirmFormDefinitionRequest) => ({
    ...(requestData.title !== undefined ? { title: requestData.title } : {}),
    ...(requestData.fields !== undefined ? { fields: requestData.fields } : {}),
    ...(requestData.settings !== undefined
      ? { settings: requestData.settings }
      : {}),
  }),
} as const satisfies BackendRouteDefinition;

export const publishFirmFormDefinitionRoute = {
  transport: 'api',
  method: 'POST',
  path: '/firms/form-definitions/:formId/publish',
  params: (requestData: { formId: string }) => ({ formId: requestData.formId }),
  body: () => ({}),
} as const satisfies BackendRouteDefinition;
